import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as LinkDeRota, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { CAMINHO_DA_FILA_DE_TRIAGEM } from '../../shared/navegacao/mapaDeNavegacao';
import { ErroDaApi } from '../../shared/api/erros';
import { formatarDataHora } from '../../shared/dominio/datas';
import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../shared/ui/EstadoVazio';
import { Identificador } from '../../shared/ui/Identificador';
import { ConferenciaDeTipoEOperadora } from './ConferenciaDeTipoEOperadora';
import { IdentificacaoDaSolicitacao } from './IdentificacaoDaSolicitacao';
import { ResultadoDoRetorno } from './ResultadoDoRetorno';
import { ReleituraDoEmail } from './demo/ReleituraDoEmail';
import { EmailLegivel, type ResultadoNoEmail } from './email/EmailLegivel';
import type { BuscaNoEmail, ModoDaBusca } from './email/marcarTrecho';
import { confirmarCampo } from './api/confirmarCampo';
import {
  opcoesDaTriagemDetalhada,
  opcoesDoEmailDaThread,
  opcoesDoEmailOriginalDaTriagem,
  urlDoAnexo,
  urlDoAnexoDaThread,
  urlDoEmailDaThread,
  urlDoEmailOriginal,
} from './api/filaDeTriagem';
import {
  ROTULOS_DE_ORIGEM,
  type AnexoDaTriagem,
  type CampoDaTriagem,
  type MensagemDaTriagem,
} from '../../shared/triagem/tipos';

/** `1234` → `"1.2 KB"`. Sem biblioteca nova — é uma conta só. */
function formatarTamanho(bytes: number | null): string {
  if (bytes === null) return 'tamanho desconhecido';
  if (bytes < 1024) return `${String(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/** `AC-29`: nunca tentado é texto DIFERENTE de tentado-e-não-encontrado. */
function textoDoCampo(campo: CampoDaTriagem, valorRevisadoLocalmente: string | undefined): string {
  if (!campo.tentado) return 'O interpretador não tentou ler este campo.';
  if (!campo.encontrado) return 'Tentou ler, não encontrou.';
  return valorRevisadoLocalmente ?? campo.valorHumano ?? campo.valor ?? 'Encontrado, sem valor.';
}

/**
 * `TP-030`, correção do bug pré-existente achado pela revisão arquitetural:
 * o predicado antigo (`confianca !== null && limiar !== null && confianca <
 * limiar`) NUNCA era verdadeiro no pipeline real, porque o extrator nunca
 * grava `limiar` (`SEM_LIMIAR = null` sempre, `mapear-interpretacao.ts:60`).
 * O caso real de revisão é `confianca === null` (campo do agente, não
 * medido) — MESMO predicado que já governa a fila
 * (`ix_campo_extraido_fila_revisao`/`PrecisaConfirmar`,
 * `TriagemEndpoints.cs:253-254`). Trocado aqui para bater com o resto do
 * sistema.
 */
function precisaConfirmar(campo: CampoDaTriagem): boolean {
  return campo.confianca === null || (campo.limiar !== null && campo.confianca < campo.limiar);
}

/**
 * O formulário de correção (`TP-030`) — só aparece quando o campo tem `id`
 * (o DETALHE expõe; `cid`/`hd` e itens de lista nunca têm, `D2`/`FC-4`) E
 * ainda não foi revisado nesta sessão de tela.
 */
function AcaoDeConfirmar({
  ticketId,
  campoId,
  aoConfirmar,
}: {
  ticketId: string;
  campoId: string;
  aoConfirmar: (campoId: string, valorHumano: string) => void;
}): React.JSX.Element {
  const navegar = useNavigate();
  const [valor, setValor] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [mensagemDeErro, setMensagemDeErro] = useState<string | null>(null);

  const confirmar = (): void => {
    const valorAEnviar = valor.trim();
    if (valorAEnviar === '') return;

    setEnviando(true);
    setMensagemDeErro(null);
    confirmarCampo(ticketId, campoId, valorAEnviar)
      .then((resultado) => {
        aoConfirmar(resultado.campoId, valorAEnviar);
      })
      .catch((causa: unknown) => {
        if (causa instanceof ErroDaApi && causa.status === 401) {
          // Mesmo tratamento que a leitura já dá para sessão sem
          // autenticação (`RotaProtegida`): sem sessão válida, não há o
          // que mostrar aqui — a pessoa precisa entrar de novo.
          void navegar('/entrar', { replace: true });
          return;
        }
        if (causa instanceof ErroDaApi && causa.status === 409) {
          setMensagemDeErro('Já revisado por outra pessoa. Recarregue a página para ver o valor atual.');
          return;
        }
        // 400 (não deveria acontecer — a UI já esconde a ação em campo
        // clínico — mas o backend é a fonte de verdade) e qualquer outro
        // erro caem na mesma mensagem genérica.
        setMensagemDeErro('Não foi possível confirmar este campo. Tente novamente.');
      })
      .finally(() => {
        setEnviando(false);
      });
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
      <TextField
        size="small"
        variant="outlined"
        placeholder="Valor corrigido"
        value={valor}
        disabled={enviando}
        onChange={(evento) => {
          setValor(evento.target.value);
        }}
        // `aria-label` direto no `TextField` rotula o `FormControl` (o
        // `div` em volta), não o `<input>` — `slotProps.htmlInput` é o
        // jeito correto do MUI de rotular o elemento nativo de verdade.
        // `maxLength` espelha o teto do handler (`ConfirmarCampoDaTriagemHandler`,
        // achado da revisão de segurança do PR #28).
        slotProps={{ htmlInput: { 'aria-label': 'Valor corrigido', maxLength: 500 } }}
      />
      <Button size="small" variant="outlined" disabled={enviando || valor.trim() === ''} onClick={confirmar}>
        Confirmar
      </Button>
      {mensagemDeErro !== null ? (
        <Typography variant="caption" color="error">
          {mensagemDeErro}
        </Typography>
      ) : null}
    </Stack>
  );
}

function Campo({
  rotulo,
  campo,
  ticketId,
  valorRevisadoLocalmente,
  aoConfirmar,
  corrigivelPorTexto = true,
  modoDaBusca = 'texto',
  aoApontar,
}: {
  rotulo: string;
  campo: CampoDaTriagem;
  ticketId: string;
  valorRevisadoLocalmente: string | undefined;
  aoConfirmar: (campoId: string, valorHumano: string) => void;
  /**
   * `false` em tipo e operadora (`TP-038`, `D5`): eles se corrigem escolhendo na lista, em "Tipo e operadora", e a
   * API recusa texto livre neles. O campo só mostra o que foi lido.
   */
  corrigivelPorTexto?: boolean;
  /** Como achar o valor no e-mail: `digitos` para número escrito com ou sem pontuação (CPF, telefone, data). */
  modoDaBusca?: ModoDaBusca;
  /** Mouse ou foco no campo: o e-mail ao lado marca de onde o valor saiu (`null` ao sair). */
  aoApontar?: (apontado: CampoApontado | null) => void;
}): React.JSX.Element {
  const foiRevisadoNestaTela = valorRevisadoLocalmente !== undefined;
  const valorExibido = valorRevisadoLocalmente ?? campo.valorHumano ?? campo.valor;
  const apontar =
    aoApontar !== undefined && campo.tentado && campo.encontrado && valorExibido !== null && valorExibido.trim() !== ''
      ? (): void => {
          aoApontar({ rotulo, busca: { valor: valorExibido, modo: modoDaBusca } });
        }
      : undefined;
  const soltar = apontar === undefined || aoApontar === undefined ? undefined : (): void => {
    aoApontar(null);
  };
  const mostrarAcaoDeConfirmar =
    corrigivelPorTexto && campo.id !== null && precisaConfirmar(campo) && campo.valorHumano === null && !foiRevisadoNestaTela;

  return (
    <Box onMouseEnter={apontar} onMouseLeave={soltar} onFocus={apontar} onBlur={soltar}>
      <Typography variant="caption" color="text.secondary" component="div">
        {rotulo}
      </Typography>
      <Typography variant="body2" component="div">
        {textoDoCampo(campo, valorRevisadoLocalmente)}
      </Typography>
      {campo.tentado && campo.encontrado ? (
        <Stack direction="row" spacing={1} sx={{ mt: 0.25 }} flexWrap="wrap">
          {campo.confianca !== null ? (
            <Typography variant="caption" color="text.secondary">
              confiança: {(campo.confianca * 100).toFixed(0)}%
            </Typography>
          ) : null}
          {campo.limiar !== null ? (
            <Typography variant="caption" color="text.secondary">
              limiar: {(campo.limiar * 100).toFixed(0)}%
            </Typography>
          ) : null}
          {campo.origem !== null ? (
            <Typography variant="caption" color="text.secondary">
              origem: {ROTULOS_DE_ORIGEM[campo.origem]}
            </Typography>
          ) : null}
          {/* D6: o rótulo de "revisado" nasce de valorHumano/revisão local —
              NUNCA de `origem`, que continua sendo a da máquina mesmo depois
              de confirmado. */}
          {corrigivelPorTexto && precisaConfirmar(campo) && !foiRevisadoNestaTela && campo.valorHumano === null ? (
            <Chip label="A confirmar" size="small" color="warning" variant="outlined" />
          ) : null}
          {foiRevisadoNestaTela || campo.valorHumano !== null ? (
            <Chip label="Revisado" size="small" color="success" variant="outlined" />
          ) : null}
        </Stack>
      ) : null}
      {mostrarAcaoDeConfirmar ? <AcaoDeConfirmar ticketId={ticketId} campoId={campo.id} aoConfirmar={aoConfirmar} /> : null}
    </Box>
  );
}

/** O campo sob o mouse e o que procurar dele no e-mail. */
interface CampoApontado {
  readonly rotulo: string;
  readonly busca: BuscaNoEmail;
}

/** O resultado da busca, em palavras: quem confere precisa saber também quando o valor NÃO está no texto. */
function textoDaMarcacao(apontado: CampoApontado, resultado: ResultadoNoEmail | null): string {
  if (resultado === null) return '';
  const { ocorrencias, pedacos } = resultado;
  if (ocorrencias > 0) {
    return `${apontado.rotulo}: marcado ${ocorrencias === 1 ? '1 vez' : `${String(ocorrencias)} vezes`} no e-mail.`;
  }
  if (pedacos !== null && pedacos.achados > 0) {
    return `${apontado.rotulo}: não aparece inteiro no e-mail; ${String(pedacos.achados)} de ${String(pedacos.total)} pedaços marcados.`;
  }
  return `${apontado.rotulo}: não aparece no texto do e-mail (pode ter vindo de um anexo ou de uma imagem).`;
}

/**
 * As mensagens da thread do ticket (`US-035` `AC9`): uma linha por mensagem, a escolhida abre ao lado.
 * Só o assunto e a hora — o app não guarda remetente, então a lista não diz quem escreveu.
 */
function ListaDeMensagens({
  mensagens,
  escolhidaId,
  aoEscolher,
}: {
  mensagens: readonly MensagemDaTriagem[];
  escolhidaId: string;
  aoEscolher: (mensagemId: string) => void;
}): React.JSX.Element {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="div">
        Mensagens deste ticket ({String(mensagens.length)})
      </Typography>
      <List dense disablePadding aria-label="Mensagens deste ticket">
        {mensagens.map((mensagem) => (
          // `ListItem` em volta: `ListItemButton` renderiza um `div role="button"`, e um `ul` só pode ter `li` filhos.
          <ListItem key={mensagem.id} disablePadding>
            <ListItemButton
              selected={mensagem.id === escolhidaId}
              aria-current={mensagem.id === escolhidaId ? 'true' : undefined}
              onClick={() => {
                aoEscolher(mensagem.id);
              }}
            >
              <ListItemText
                primary={`${formatarDataHora(mensagem.recebidaEm)}${mensagem.ehOrigem ? ' · origem' : ''}`}
                secondary={mensagem.assunto === null || mensagem.assunto.trim() === '' ? '(sem assunto)' : mensagem.assunto}
                slotProps={{ secondary: { noWrap: true } }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        Os campos lidos ao lado vêm do e-mail de origem.
      </Typography>
    </Box>
  );
}

/** Um anexo, com link de download por sessão (`AC-18`, `T16`; `AC9` para mensagem fora da origem). */
function ListaDeAnexo({ anexo, href }: { anexo: AnexoDaTriagem; href: string }): React.JSX.Element {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" component="a" href={href} download>
        {anexo.nomeArquivo}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        ({formatarTamanho(anexo.tamanhoBytes)})
      </Typography>
    </Stack>
  );
}

/**
 * O detalhe da triagem (`AC-17`-`AC-19`, `AC-28`-`AC-31`): a leitura ao
 * lado do e-mail de origem.
 *
 * Quatro estados, mesma disciplina de `DetalheDoCaso`: carregando, erro,
 * vazio (404 → `null`, resolvido em `T11`) e dados.
 */
export function DetalheDaTriagem(): React.JSX.Element {
  const { ticketId = '' } = useParams<{ ticketId: string }>();
  const consulta = useQuery(opcoesDaTriagemDetalhada(ticketId));

  // `US-035` `AC9`: com mais de uma mensagem a pessoa escolhe qual abrir. Sem escolha, a de origem —
  // o comportamento de antes. `mensagemFora` só existe quando a escolhida NÃO é a de origem: é ela
  // que troca a rota do `.eml` e dos anexos.
  const mensagens = consulta.data?.mensagens ?? [];
  // A escolha guarda o ticket a que pertence: navegar para outro ticket (o componente continua montado) não
  // reaproveita a mensagem escolhida no anterior.
  const [escolha, definirEscolha] = useState<{ ticketId: string; mensagemId: string } | null>(null);
  // `TP-072`: a fonte de uma informação do Caso chega com `?mensagem=<emailId>` e abre aquela mensagem.
  const [parametros] = useSearchParams();
  const mensagemEscolhidaId = (escolha?.ticketId === ticketId ? escolha.mensagemId : null) ?? parametros.get('mensagem');
  const escolhida =
    mensagens.length > 1
      ? (mensagens.find((m) => m.id === mensagemEscolhidaId) ?? mensagens.find((m) => m.ehOrigem) ?? mensagens[0] ?? null)
      : null;
  const mensagemFora = escolhida !== null && !escolhida.ehOrigem ? escolhida : null;

  const consultaDoEmailDeOrigem = useQuery({
    ...opcoesDoEmailOriginalDaTriagem(ticketId),
    enabled: consulta.data?.temEmailDeOrigem === true && mensagemFora === null,
  });
  const consultaDaMensagem = useQuery({
    ...opcoesDoEmailDaThread(ticketId, mensagemFora?.id ?? ''),
    enabled: mensagemFora !== null,
  });
  const consultaDoEmail = mensagemFora === null ? consultaDoEmailDeOrigem : consultaDaMensagem;
  // A releitura vale para a mensagem aberta: a escolhida da thread, ou a de origem.
  const mensagemAberta = mensagemFora ?? mensagens.find((m) => m.ehOrigem) ?? mensagens[0] ?? null;
  const emailDaReleitura = mensagemAberta?.id ?? null;
  const hrefDoEml = mensagemFora === null ? urlDoEmailOriginal(ticketId) : urlDoEmailDaThread(ticketId, mensagemFora.id);
  const hrefDoAnexo = (anexoId: string): string =>
    mensagemFora === null ? urlDoAnexo(ticketId, anexoId) : urlDoAnexoDaThread(ticketId, mensagemFora.id, anexoId);

  const detalhe = consulta.data ?? null;
  const anexosExibidos = mensagemFora === null ? (detalhe?.anexos ?? []) : mensagemFora.anexos;

  // `TP-030`: os campos confirmados NESTA tela, por `campoId` — atualiza o
  // estado local ao confirmar (o contrato de leitura não devolve
  // `revisado_em`, só `valorHumano`/confiança; refazer a consulta reler do
  // mock/API estático não é o que prova a ação, D6/Sumário do tech-plan).
  const [camposRevisadosNestaTela, definirCamposRevisadosNestaTela] = useState<Record<string, string>>({});
  const confirmarNoEstadoLocal = (campoId: string, valorHumano: string): void => {
    definirCamposRevisadosNestaTela((atual) => ({ ...atual, [campoId]: valorHumano }));
  };
  // O campo sob o mouse e o que a busca do valor dele achou no e-mail (`null` enquanto não há busca).
  const [campoApontado, definirCampoApontado] = useState<CampoApontado | null>(null);
  const [resultadoNoEmail, definirResultadoNoEmail] = useState<ResultadoNoEmail | null>(null);

  const propsDeRevisao = (
    campo: CampoDaTriagem,
  ): {
    ticketId: string;
    valorRevisadoLocalmente: string | undefined;
    aoConfirmar: (campoId: string, valorHumano: string) => void;
    aoApontar: (apontado: CampoApontado | null) => void;
  } => ({
    ticketId,
    valorRevisadoLocalmente: campo.id === null ? undefined : camposRevisadosNestaTela[campo.id],
    aoConfirmar: confirmarNoEstadoLocal,
    aoApontar: definirCampoApontado,
  });

  return (
    <Stack spacing={2}>
      <Button
        component={LinkDeRota}
        to={CAMINHO_DA_FILA_DE_TRIAGEM}
        startIcon={<ArrowBackOutlined />}
        sx={{ alignSelf: 'start' }}
      >
        Voltar para a fila
      </Button>

      {consulta.isPending ? <EstadoDeCarregamento linhas={4} /> : null}

      {consulta.isError ? (
        <EstadoDeErro
          erro={consulta.error}
          contexto="Não foi possível carregar a triagem"
          aoReprocessar={() => {
            void consulta.refetch();
          }}
        />
      ) : null}

      {/* A API respondeu 200: o ticket é que não existe, não é de triagem,
          ou é de núcleo alheio — os três casos dão a MESMA resposta (T5/T7),
          e viram o mesmo estado vazio aqui (o 404 já virou `null` em T11). */}
      {consulta.isSuccess && detalhe === null ? (
        <EstadoVazio
          titulo="Triagem não encontrada"
          descricao="A API respondeu normalmente, mas não há ticket de triagem com este identificador."
        />
      ) : null}

      {consulta.isSuccess && detalhe !== null ? (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Stack spacing={1.5} sx={{ height: '100%' }}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Typography variant="h2">
                    {mensagemFora === null ? 'E-mail de origem' : `Mensagem de ${formatarDataHora(mensagemFora.recebidaEm)}`}
                  </Typography>
                  <Identificador valor={detalhe.codigoCurto ?? detalhe.id} />
                  <IdentificacaoDaSolicitacao
                    numeroSiai={detalhe.numeroSiai}
                    contagemDeMensagens={detalhe.contagemDeMensagens}
                    ultimaMensagemEm={detalhe.ultimaMensagemEm}
                  />
                </Stack>
                <Divider />

                {!detalhe.temEmailDeOrigem ? (
                  <Typography variant="body2" color="text.secondary">
                    Este ticket ainda não tem e-mail de origem vinculado.
                  </Typography>
                ) : null}

                {detalhe.temEmailDeOrigem ? (
                  <>
                    {escolhida !== null ? (
                      <ListaDeMensagens
                        mensagens={mensagens}
                        escolhidaId={escolhida.id}
                        aoEscolher={(mensagemId) => {
                          definirEscolha({ ticketId, mensagemId });
                        }}
                      />
                    ) : null}

                    {/* TP-056: o resultado do retorno da mensagem aberta, ao lado do .eml que traz a carta. */}
                    {mensagemAberta !== null ? (
                      <ResultadoDoRetorno key={mensagemAberta.id} ticketId={ticketId} mensagem={mensagemAberta} />
                    ) : null}

                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                      <Button
                        component="a"
                        href={hrefDoEml}
                        download
                        startIcon={<DownloadOutlined />}
                        variant="outlined"
                        size="small"
                      >
                        {mensagemFora === null ? 'Baixar .eml original' : 'Baixar .eml desta mensagem'}
                      </Button>
                      {emailDaReleitura !== null ? (
                        <ReleituraDoEmail ticketId={ticketId} emailId={emailDaReleitura} />
                      ) : null}
                    </Stack>

                    {consultaDoEmail.isPending ? <EstadoDeCarregamento linhas={6} /> : null}

                    {consultaDoEmail.isError ? (
                      <EstadoDeErro
                        erro={consultaDoEmail.error}
                        contexto="Não foi possível carregar o e-mail de origem"
                        aoReprocessar={() => {
                          void consultaDoEmail.refetch();
                        }}
                      />
                    ) : null}

                    {consultaDoEmail.isSuccess && consultaDoEmail.data === null ? (
                      <Typography variant="body2" color="text.secondary">
                        A API respondeu, mas não há conteúdo de e-mail para este ticket.
                      </Typography>
                    ) : null}

                    {consultaDoEmail.isSuccess && consultaDoEmail.data !== null ? (
                      <>
                        {/* Os campos ao lado vêm do e-mail de origem: só nele faz sentido marcar de onde saíram. */}
                        {mensagemFora === null ? (
                          <Typography variant="caption" color="text.secondary" aria-live="polite">
                            {campoApontado === null
                              ? 'Passe o mouse num campo ao lado para ver de onde ele saiu.'
                              : textoDaMarcacao(campoApontado, resultadoNoEmail)}
                          </Typography>
                        ) : null}
                        <EmailLegivel
                          eml={consultaDoEmail.data}
                          busca={mensagemFora === null ? (campoApontado?.busca ?? null) : null}
                          aoMarcar={definirResultadoNoEmail}
                        />
                      </>
                    ) : null}

                    {/* T16: `attachments` do contrato, nome+tamanho, `embutido`
                        (assinatura/logo) já excluído pelo backend. */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" component="div">
                        Anexos
                      </Typography>
                      {anexosExibidos.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Nenhum anexo neste e-mail.
                        </Typography>
                      ) : (
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                          {anexosExibidos.map((anexo) => (
                            <ListaDeAnexo key={anexo.id} anexo={anexo} href={hrefDoAnexo(anexo.id)} />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </>
                ) : null}
              </Stack>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Stack spacing={1.5}>
                <Typography variant="h2">O que a leitura entendeu</Typography>
                <Divider />

                {detalhe.temEmailDeOrigem ? (
                  <>
                    <ConferenciaDeTipoEOperadora
                      key={ticketId}
                      ticketId={ticketId}
                      classificacao={detalhe.classificacao}
                      operadorasParaEscolha={detalhe.operadorasParaEscolha}
                      tipoLido={detalhe.tipoDeSolicitacao}
                      operadoraLida={detalhe.operadora}
                      aoAtualizar={() => {
                        void consulta.refetch();
                      }}
                    />
                    <Divider />
                  </>
                ) : null}

                {/* Os DEZESSETE campos com valor (TP-026 acrescenta data de
                    nascimento e responsável; TP-027 acrescenta telefone,
                    endereço, dispositivo, solicitante e liminar; TP-029
                    acrescenta frequência). `cid`/`hd` (AC-31, TP-027) não
                    entram nesta lista — o tipo `CampoClinico` não tem
                    propriedade de valor, e o componente não tem como
                    renderizar por acidente o que o tipo não carrega. */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo rotulo="Paciente" campo={detalhe.paciente} {...propsDeRevisao(detalhe.paciente)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo rotulo="CPF" campo={detalhe.cpf} {...propsDeRevisao(detalhe.cpf)} modoDaBusca="digitos" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo rotulo="Matrícula" campo={detalhe.matricula} {...propsDeRevisao(detalhe.matricula)} modoDaBusca="digitos" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo rotulo="Data de nascimento" campo={detalhe.dataNascimento} {...propsDeRevisao(detalhe.dataNascimento)} modoDaBusca="digitos" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo rotulo="Responsável" campo={detalhe.responsavel} {...propsDeRevisao(detalhe.responsavel)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo
                      rotulo="Operadora"
                      campo={detalhe.operadora}
                      {...propsDeRevisao(detalhe.operadora)}
                      corrigivelPorTexto={false}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo rotulo="Hospital" campo={detalhe.hospital} {...propsDeRevisao(detalhe.hospital)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo
                      rotulo="Tipo de solicitação"
                      campo={detalhe.tipoDeSolicitacao}
                      {...propsDeRevisao(detalhe.tipoDeSolicitacao)}
                      corrigivelPorTexto={false}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo rotulo="Prazo" campo={detalhe.prazo} {...propsDeRevisao(detalhe.prazo)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo rotulo="Protocolo" campo={detalhe.protocolo} {...propsDeRevisao(detalhe.protocolo)} modoDaBusca="digitos" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo rotulo="Telefone" campo={detalhe.telefone} {...propsDeRevisao(detalhe.telefone)} modoDaBusca="digitos" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo rotulo="Endereço" campo={detalhe.endereco} {...propsDeRevisao(detalhe.endereco)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo rotulo="Dispositivo" campo={detalhe.dispositivo} {...propsDeRevisao(detalhe.dispositivo)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo rotulo="Solicitante" campo={detalhe.solicitante} {...propsDeRevisao(detalhe.solicitante)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo rotulo="Liminar" campo={detalhe.liminar} {...propsDeRevisao(detalhe.liminar)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Campo rotulo="Frequência" campo={detalhe.frequencia} {...propsDeRevisao(detalhe.frequencia)} />
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary" component="div">
                      Parâmetros solicitados
                    </Typography>
                    {detalhe.parametrosSolicitados.length === 0 ? (
                      <Typography variant="body2">Nenhum parâmetro lido.</Typography>
                    ) : (
                      <Stack spacing={1} sx={{ mt: 0.5 }}>
                        {detalhe.parametrosSolicitados.map((parametro, indice) => (
                          // Lista somente-leitura, sem id próprio no contrato — a ordem é o que o backend garante
                          // (FC-4, TP-030 não muda isto: `propsDeRevisao` nunca acha `id` aqui, então a ação
                          // de confirmar nunca aparece para item de lista).
                          <Campo
                            key={`parametro-${String(indice)}`}
                            rotulo={`Parâmetro ${String(indice + 1)}`}
                            campo={parametro}
                            {...propsDeRevisao(parametro)}
                          />
                        ))}
                      </Stack>
                    )}
                  </Grid>
                </Grid>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      ) : null}
    </Stack>
  );
}
